import React from 'react';
import ReactTooltip from 'react-tooltip';
import TimeAgo from 'javascript-time-ago';
import en from 'javascript-time-ago/locale/en.json';
import { useHistory } from 'react-router-dom';

import ActionsMenu from '@components/ActionsMenu/ActionsMenu';
import LanguageSwitcher from '@uikit/LanguageSwitcher/LanguageSwitcher';
import { Avatar } from '@uikit/Avatar/Avatar.stories';
import { Tr } from '@uikit/old/Table/Table';
import { SVGIcon, SvgIconType } from '@uikit/old/Icon/Icon';
import ColoredStatusToggler from '@uikit/ColoredStatusToggler/ColoredStatusToggler';

import { PublishersWebsiteType } from '@ts/publishers.types';
import { PublisherWebsiteStatus } from 'respona_api/generated/common_pb';
import { formatMetrics } from '@helpers/opportunitiesHelpers';

// @ts-ignore
import ahrefsImg from 'src/assets/ahrefs-logo-square.png';
// @ts-ignore
import mozImg from 'src/assets/moz-logo-square.png';
// @ts-ignore
import semrushImg from 'src/assets/semrush-logo-square.jpeg';
import { redirectUserTo } from '@utils/historyHandler';
import urls from '@constants/urls';
import useCurrentWorkspaceId from '@hooks/useCurrentWorkspaceId';
import TimestampToDate from '@uikit/TimestampToDate/TimestampToDate';

import { getWebsiteStatusConfig, WEBSITE_VERIFIED_TOOLTIP_ID_PREFIX } from './constants';

// addDefaultLocale() may run only once per page (RES-7334); both Publisher tables register it.
TimeAgo.addLocale(en);
TimeAgo.setDefaultLocale(en.locale);

type TableRenderProps = {
  row: { createdByName: string } & PublishersWebsiteType;
  children?: any;
  renderProps?: {
    [key: string]: any;
  };
  onRowClick: () => void;
};

const languages = [
  { code: 'en', countryCode: 'gb', label: 'English' },
  { code: 'de', countryCode: 'de', label: 'Deutsch' },
  { code: 'fr', countryCode: 'fr', label: 'Français' },
  { code: 'it', countryCode: 'it', label: 'Italiano' },
  { code: 'ja', countryCode: 'jp', label: '日本語' },
];

export default [
  {
    title: 'Status',
    dataKey: 'status',
    RenderCell: ({ row, renderProps }: TableRenderProps): JSX.Element => {
      const statusConfig = getWebsiteStatusConfig(row.status);

      return (
        <div className="website-status">
          <ColoredStatusToggler
            id={row.id}
            name="website"
            title={statusConfig.label}
            color={statusConfig.bg}
            titleColor={statusConfig.text}
            icon={statusConfig.icon}
            iconColor={statusConfig.iconColor}
            iconSize={null}
            status={row.status}
            tooltipText={statusConfig.tooltip}
            isActive={row.status === PublisherWebsiteStatus.PW_ACTIVE}
            onChange={() => renderProps.onUpdateStatus(row.id, statusConfig.nextStatus)}
            testId={`publisher-websites-row-status-${row.domain}`}
          />
        </div>
      );
    },
  },
  {
    title: 'Website',
    dataKey: 'website',
    RenderCell: ({ row }: TableRenderProps): JSX.Element => {
      const currentWorkspaceId = useCurrentWorkspaceId();
      const history = useHistory();

      let parsedURL = row.domain;

      if (!parsedURL.startsWith('http://') && !parsedURL.startsWith('https://')) {
        parsedURL = `https://${parsedURL}`;
      }

      const handleBadgeClick = (event: React.MouseEvent) => {
        // The row opens the price step on click; the badge goes to verification instead.
        event.stopPropagation();
        redirectUserTo(history, `${urls.WEBSITE_STEP_FOUR(currentWorkspaceId, row.id)}`);
      };

      return (
        <div className="title-cell">
          <div className="title-cell__body orders-table-website row-direction">
            <Avatar isSquared url={row.logo} fullName={row.name} size="xs" />
            <div className="title-cell__title" title={row.name}>
              <div className="row-direction">
                <p>{row.name}</p>
                <span
                  className={`cell-color-label ${row.verified ? 'green-label' : 'orange-label'}`}
                  onClick={handleBadgeClick}
                  data-testid={`publisher-websites-row-verified-${row.domain}`}
                  data-verified={row.verified ? 'true' : 'false'}
                >
                  <SVGIcon
                    icon={row.verified ? 'checkCircled' : 'warningTriangleCircle'}
                    size={12}
                  />
                  <span data-for={`${WEBSITE_VERIFIED_TOOLTIP_ID_PREFIX}${row.id}`} data-tip="">
                    {row.verified ? 'Verified' : 'Unverified'}
                  </span>
                  <ReactTooltip
                    className="orders-table-tooltip"
                    id={`${WEBSITE_VERIFIED_TOOLTIP_ID_PREFIX}${row.id}`}
                    place="top"
                    effect="solid"
                    arrowColor="transparent"
                  >
                    <div className="orders-table-tooltip__body">
                      {row.verified ? (
                        <>
                          <p>Verified via DNS.</p>
                          <p>Higher order priority</p>
                        </>
                      ) : (
                        <>
                          <p>Verified sites receive up to 2X</p>
                          <p>the number of orders.</p>
                        </>
                      )}
                    </div>
                  </ReactTooltip>
                </span>
              </div>
              <a
                key={row.id}
                href={parsedURL}
                target="_blank"
                rel="noopener noreferrer"
                className="link"
              >
                {row.domain}
              </a>
            </div>
          </div>
        </div>
      );
    },
  },
  // {
  //   title: 'Language(s)',
  //   dataKey: 'languages',
  //   RenderCell: ({ row }: TableRenderProps): JSX.Element => {
  //     return (
  //       <div className="orders-table-langs">
  //         <p>
  //           {row.languagesList.map((langCode, index) => {
  //             const langObj = languages.find((l) => l.code === langCode);
  //             if (!langObj) return null;
  //
  //             return (
  //               <LanguageSwitcher
  //                 languages={[langObj]}
  //                 onChange={(newLangCode) => {
  //                   console.log(`Row ${row.id}, lang index ${index} changed to`, newLangCode);
  //                 }}
  //               />
  //             );
  //           })}
  //         </p>
  //       </div>
  //     );
  //   },
  // },
  // {
  //   title: 'Quality score',
  //   dataKey: 'quality',
  //   RenderCell: ({ row }: TableRenderProps): JSX.Element => {
  //     const value = row.qualityScore;
  //
  //     const getBackgroundColor = (val: number) => {
  //       if (val < 70) return '#FFD8D8';
  //       if (val < 90) return '#FFF1E2';
  //       return '#ECFFF3';
  //     };
  //
  //     const getTextColor = (val: number) => {
  //       if (val < 70) return '#ED483D';
  //       if (val < 90) return '#FC8E0E';
  //       return '#1ACD61';
  //     };
  //
  //     const styles: React.CSSProperties = {};
  //
  //     styles.backgroundColor = getBackgroundColor(value);
  //     styles.color = getTextColor(value);
  //
  //     return (
  //       <div className="orders-table-block">
  //         <div style={styles} className="colored-block">
  //           {value}%
  //         </div>
  //       </div>
  //     );
  //   },
  // },
  {
    title: 'Article price',
    dataKey: 'article',
    RenderCell: ({ row }: TableRenderProps): JSX.Element => {
      return (
        <div className="orders-table-article">
          <p data-testid={`publisher-websites-row-price-${row.domain}`}>${row.price}</p>
        </div>
      );
    },
  },
  {
    title: '# of links',
    dataKey: 'links',
    RenderCell: ({ row }: TableRenderProps): JSX.Element => {
      return (
        <div className="orders-table-article">
          <p data-testid={`publisher-websites-row-links-${row.domain}`}>{row.linkLimit}</p>
        </div>
      );
    },
  },
  {
    title: 'Metrics',
    dataKey: 'metrics',
    RenderCell: ({ row }: TableRenderProps): JSX.Element => {
      const { ahrefRating, mozAuthority, semrushRank } = row.metrics;

      return (
        <div className="orders-table-metrics">
          <div>
            <img src={ahrefsImg} alt="" width={15} height={15} />
            {ahrefRating !== -1 ? <span>dr</span> : null}
            {formatMetrics(ahrefRating)}
          </div>
          <div>
            <img src={semrushImg} alt="" width={15} height={15} />
            {semrushRank !== -1 ? <span>as</span> : null}
            {formatMetrics(semrushRank)}
          </div>
          <div>
            <img src={mozImg} alt="" width={15} height={15} />
            {mozAuthority !== -1 ? <span>da</span> : null}
            {formatMetrics(mozAuthority)}
          </div>
        </div>
      );
    },
  },
  // {
  //   title: 'Country',
  //   dataKey: 'country',
  //   RenderCell: ({ row }: TableRenderProps): JSX.Element => {
  //     const { countryPercentage, topCountry } = row.metrics;
  //     return (
  //       <div className="orders-table-country">
  //         <div>{topCountry}</div>
  //         <div>{countryPercentage === -1 ? '-' : `${countryPercentage}%`}</div>
  //       </div>
  //     );
  //   },
  // },
  {
    title: 'Traffic',
    dataKey: 'traffic',
    RenderCell: ({ row }: TableRenderProps): JSX.Element => {
      const { traffic } = row.metrics;
      return (
        <div className="orders-table-traffic">
          <div>{formatMetrics(traffic)}</div>
        </div>
      );
    },
  },
  {
    title: 'Added on',
    dataKey: 'addedOn',
    RenderCell: ({ row }: TableRenderProps): JSX.Element => {
      const { createdAt, createdByName } = row;
      return (
        <div className="orders-table-added-on">
          <TimestampToDate timestamp={createdAt} />
          <div className="orders-table-added-on__created-by">by {createdByName}</div>
        </div>
      );
    },
  },
  // {
  //   title: 'Keywords',
  //   dataKey: 'keywords',
  //   RenderCell: ({ row }: TableRenderProps): JSX.Element => {
  //     const { keywords } = row.metrics;
  //     return (
  //       <div className="orders-table-keywords">
  //         <div>{formatMetrics(keywords)}</div>
  //       </div>
  //     );
  //   },
  // },
  // {
  //   title: 'Ref.Domains',
  //   dataKey: 'ref-domains',
  //   RenderCell: ({ row }: TableRenderProps): JSX.Element => {
  //     const { refDomains } = row.metrics;
  //     return (
  //       <div className="orders-table-ref-domains">
  //         <div>{formatMetrics(refDomains)}</div>
  //       </div>
  //     );
  //   },
  // },
  {
    title: '',
    dataKey: 'actions',
    RenderCell: ({ row, renderProps: { onRemove, onEdit } }: TableRenderProps): JSX.Element => {
      const actionsConfig = [
        {
          icon: 'trashWhite' as SvgIconType,
          title: 'Remove',
          handler: () => onRemove(row.id),
          isConfirm: true,
          testId: `publisher-websites-row-remove-${row.domain}`,
        },
        {
          icon: 'editAction' as SvgIconType,
          title: 'Edit',
          handler: () => onEdit(row.id),
          testId: `publisher-websites-row-edit-${row.domain}`,
        },
      ];

      return (
        <ActionsMenu
          actionsConfig={actionsConfig}
          id={`website-actions-${row.id}`}
          tooltipPlace="left"
          testId={`publisher-websites-row-actions-${row.domain}`}
        />
      );
    },
  },
  // {
  //   title: 'Orders',
  //   dataKey: 'received',
  //   RenderCell: ({ row }: TableRenderProps): JSX.Element => {
  //     const item = row.s.find((stat) => stat.status === PublisherArticleStatus.TODO);
  //
  //     if (item == null) {
  //       return <div />;
  //     }
  //     return (
  //       <div className="orders-table-cell">
  //         <p>
  //           {item.itemNumber} (${item.totalPrice})
  //         </p>
  //         <span>Received</span>
  //       </div>
  //     );
  //   },
  // },
  // {
  //   title: '',
  //   dataKey: 'accepted',
  //   RenderCell: ({ row }: TableRenderProps): JSX.Element => {
  //     const item = row.statList.find(
  //       (stat) => stat.status === PublisherArticleStatus.PENDING_PUBLICATION
  //     );
  //     if (item == null) {
  //       return <div />;
  //     }
  //     return (
  //       <div className="orders-table-cell">
  //         <p>
  //           {item.itemNumber} (${item.totalPrice})
  //         </p>
  //         <span>accepted</span>
  //       </div>
  //     );
  //   },
  // },
  // {
  //   title: '',
  //   dataKey: 'live',
  //   RenderCell: ({ row }: TableRenderProps): JSX.Element => {
  //     const item = row.statList.find((stat) => stat.status === PublisherArticleStatus.PUBLISHER_ORDER_LIVE);
  //     if (item == null) {
  //       return <div />;
  //     }
  //     return (
  //       <div className="orders-table-cell">
  //         <p>
  //           {item.itemNumber} (${item.totalPrice})
  //         </p>
  //         <span>live</span>
  //       </div>
  //     );
  //   },
  // },
  // {
  //   title: '',
  //   dataKey: 'indexed',
  //   RenderCell: ({ row }: TableRenderProps): JSX.Element => {
  //     const item = row.statList.find(
  //       (stat) => stat.status === PublisherArticleStatus.ARTICLE_INDEXED
  //     );
  //     if (item == null) {
  //       return <div />;
  //     }
  //     return (
  //       <div className="orders-table-cell">
  //         <p>
  //           {item.itemNumber} (${item.totalPrice})
  //         </p>
  //         <span>Indexed (paid)</span>
  //       </div>
  //     );
  //   },
  // },
  // {
  //   title: '',
  //   dataKey: 'rejected',
  //   RenderCell: ({ row }: TableRenderProps): JSX.Element => {
  //     const item = row.statList.find(
  //       (stat) => stat.status === PublisherArticleStatus.ARTICLE_REJECTED
  //     );
  //     if (item == null) {
  //       return <div />;
  //     }
  //     return (
  //       <div className="orders-table-cell">
  //         <p>
  //           {item.itemNumber} (${item.totalPrice})
  //         </p>
  //         <span>Rejected</span>
  //       </div>
  //     );
  //   },
  // },
];

export function RenderRow({
  children,
  onRowClick,
  rowTestId,
}: TableRenderProps & { rowTestId?: string }): JSX.Element {
  return (
    <Tr className="orders-table__row" onClick={onRowClick} data-testid={rowTestId}>
      {children}
    </Tr>
  );
}
